import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Loader2, Building2, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { LanguageSwitcher } from '@/components/ui/language-switcher';
import api from '@/lib/api';
import { getApiErrorMessage } from '@/lib/toast';

type Outcome = { sent: boolean; mode: 'EMAIL' | 'ADMIN_RESET' };

/**
 * POS-I185: "Forgot password?". The server answers the same way whether or not the business or e-mail
 * exists. With e-mail off (no SMTP) the page tells the user to ask their administrator instead.
 */
export function ForgotPasswordPage() {
  const { t } = useTranslation('auth');
  const [tenantSlug, setTenantSlug] = useState('');
  const [email, setEmail] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pending) return;
    setError(null);
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      setError(t('invalidEmail'));
      return;
    }
    setPending(true);
    try {
      const res = await api.post<Outcome>('/auth/forgot-password', {
        tenantSlug: tenantSlug.trim() || undefined,
        email: email.trim(),
      });
      setOutcome(res.data);
    } catch (err) {
      setError(getApiErrorMessage(err, t('forgotFailed')));
    } finally {
      setPending(false);
    }
  };

  return (
    <Card className="border-0 shadow-xl glass-card animate-scale-in">
      <CardHeader className="space-y-1 pb-4">
        <CardTitle className="text-2xl font-bold animate-fade-in-up">{t('forgotTitle')}</CardTitle>
        <CardDescription>{t('forgotSubtitle')}</CardDescription>
      </CardHeader>
      {outcome ? (
        <>
          <CardContent className="space-y-3">
            <p role="status" className="rounded-lg border bg-muted/40 p-3 text-sm">
              {outcome.mode === 'ADMIN_RESET' ? t('forgotAdminReset') : t('forgotSent')}
            </p>
          </CardContent>
          <CardFooter className="flex flex-col gap-4 pt-2">
            <Link to="/login" className="w-full">
              <Button type="button" variant="outline" className="w-full h-11">{t('backToSignIn')}</Button>
            </Link>
          </CardFooter>
        </>
      ) : (
        <form onSubmit={submit}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="forgot-tenant" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('businessId')}
              </Label>
              <div className="relative">
                <Building2 className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="forgot-tenant"
                  placeholder="demo-store"
                  className="ps-9 h-11"
                  autoComplete="organization"
                  value={tenantSlug}
                  onChange={(e) => setTenantSlug(e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="forgot-email" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('email')}
              </Label>
              <div className="relative">
                <Mail className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="forgot-email"
                  type="email"
                  placeholder="admin@mystore.com"
                  className="ps-9 h-11"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>
            {error && <p role="alert" className="text-sm text-destructive-text">{error}</p>}
          </CardContent>
          <CardFooter className="flex flex-col gap-4 pt-2">
            <Button
              type="submit"
              className="w-full h-11 bg-gradient-to-r from-primary via-primary/90 to-primary/80 text-primary-foreground font-semibold shadow-glow"
              disabled={pending || !email.trim()}
            >
              {pending && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
              {t('forgotSend')}
            </Button>
            <p className="text-center text-sm text-muted-foreground">
              <Link to="/login" className="font-medium text-primary-text hover:underline">{t('backToSignIn')}</Link>
            </p>
            <div className="flex justify-center pt-1">
              <LanguageSwitcher size="sm" />
            </div>
          </CardFooter>
        </form>
      )}
    </Card>
  );
}
